import {
  MemberadminTFunction,
  BrandTFunction,
  useMemberadminTFunction,
} from '@dap/memberadmin/i18n';
import {
  BrandProperty,
  BrandPropertyInheritType,
  DataownerBasic,
  InformationModelType,
  InheritableProperty,
  LocationOverview,
  PropertyDetail,
  PropertyValueTypes,
  TransformedInheritablePropertyValue,
} from '@dap/memberadmin/types';
import { AppTFunction } from '@dap/common/i18n';
import { createFilterOptions, FilterOptionsState } from '@mui/material';
import { format, parseISO } from 'date-fns';

export interface KeyGroupOptionType {
  inputValue?: string;
  keyGroup: string;
}

export const keyGroupFilterOptions = createFilterOptions<KeyGroupOptionType>();

export const getKeyGroupOptions = (
  properties: Array<PropertyDetail>
): readonly KeyGroupOptionType[] => {
  return properties
    .map(({ keyGroup }) => keyGroup || '')
    .filter((keyGroup) => !!keyGroup || keyGroup !== null)
    .filter((keyGroup, index, array) => array.indexOf(keyGroup) === index)
    .map((keyGroup) => ({ keyGroup }));
};

export const getValueTypeLabel = (valueType: string, t: MemberadminTFunction) => {
  switch (valueType) {
    case PropertyValueTypes.string:
      return t('memberadmin.properties.types.string');
    case PropertyValueTypes.boolean:
      return t('memberadmin.properties.types.boolean');
    case PropertyValueTypes.number:
      return t('memberadmin.properties.types.number');
    case PropertyValueTypes.date:
      return t('memberadmin.properties.types.date');
    case PropertyValueTypes.enum:
      return t('memberadmin.properties.types.enum');
    case PropertyValueTypes.multipleChoice:
      return t('memberadmin.properties.types.multipleChoice');
    case PropertyValueTypes.list:
      return t('memberadmin.properties.types.list');
    case PropertyValueTypes.map:
      return t('memberadmin.properties.types.map');
    default:
      return '';
  }
};

export const getInheritTypeLabel = (
  inheritType: BrandPropertyInheritType,
  t: MemberadminTFunction,
  tBrand: BrandTFunction
) => {
  switch (inheritType) {
    case BrandPropertyInheritType.BRAND_ONLY:
      return t('memberadmin.properties.create.inheritable.brandOnly');
    case BrandPropertyInheritType.DATAOWNERS:
      return t('memberadmin.properties.create.inheritable.allDataowners', {
        dataowners: tBrand('brand.dataowner.name.plural'),
      });
    case BrandPropertyInheritType.LOCATIONS:
      return t('memberadmin.properties.create.inheritable.allLocations', {
        locations: tBrand('brand.location.name.plural'),
      });
    case BrandPropertyInheritType.DATAOWNERS_AND_LOCATIONS:
      return t('memberadmin.properties.create.inheritable.allDataownersAndLocations', {
        dataowners: tBrand('brand.dataowner.name.plural'),
        locations: tBrand('brand.location.name.plural'),
      });
    default:
      return '';
  }
};

export const getInheritType = (
  property: BrandProperty,
  t: MemberadminTFunction,
  tBrand: BrandTFunction
) => {
  switch (true) {
    case !property.dataownerInheritable && !property.locationInheritable:
      return getInheritTypeLabel(BrandPropertyInheritType.BRAND_ONLY, t, tBrand);

    case property.dataownerInheritable && property.locationInheritable:
      return getInheritTypeLabel(BrandPropertyInheritType.DATAOWNERS_AND_LOCATIONS, t, tBrand);

    case property.dataownerInheritable:
      return getInheritTypeLabel(BrandPropertyInheritType.DATAOWNERS, t, tBrand);

    case property.locationInheritable:
      return getInheritTypeLabel(BrandPropertyInheritType.LOCATIONS, t, tBrand);
    default:
      return '';
  }
};

export const getInheritableType = (property: BrandProperty | InheritableProperty) => {
  switch (true) {
    case !property.dataownerInheritable && !property.locationInheritable:
      return BrandPropertyInheritType.BRAND_ONLY;

    case property.dataownerInheritable && property.locationInheritable:
      return BrandPropertyInheritType.DATAOWNERS_AND_LOCATIONS;

    case property.dataownerInheritable:
      return BrandPropertyInheritType.DATAOWNERS;

    case property.locationInheritable:
      return BrandPropertyInheritType.LOCATIONS;
  }
  return '';
};

export const getColumnDefType = (valueType: string) => {
  switch (valueType) {
    case PropertyValueTypes.string:
      return 'string';
    case PropertyValueTypes.boolean:
      return 'boolean';
    case PropertyValueTypes.number:
      return 'number';
    case PropertyValueTypes.date:
      return 'date';
    case PropertyValueTypes.enum:
      return 'singleSelect';
    case PropertyValueTypes.multipleChoice:
      return 'string';
    case PropertyValueTypes.list:
      return 'string';
    case PropertyValueTypes.map:
      return 'string';
    default:
      return 'string';
  }
};

export const useTransformValueTypesToReadableLabel = (valueTypes: Array<string> | undefined) => {
  const t = useMemberadminTFunction();

  if (!valueTypes) return [];

  return valueTypes.map((valueType) => ({
    name: getValueTypeLabel(valueType, t),
    value: valueType,
  }));
};

export const getOptionLabel = (option: string | KeyGroupOptionType | null) => {
  if (option === null) return '';
  if (typeof option === 'string') return option;
  if (option.inputValue) return option.inputValue;
  return option.keyGroup;
};

export const getFilterOptions = (
  options: KeyGroupOptionType[],
  params: FilterOptionsState<KeyGroupOptionType>,
  addText: string
) => {
  const filtered = keyGroupFilterOptions(options, params);
  const { inputValue } = params;
  const isExisting = options.some((option) => inputValue === option.keyGroup);
  if (inputValue !== '' && !isExisting) {
    filtered.push({
      inputValue,
      keyGroup: `${addText} "${inputValue}"`,
    });
  }
  return filtered;
};

export const propertyValueGetter = (value: string, valueType: string, t: AppTFunction) => {
  switch (valueType) {
    case PropertyValueTypes.string:
      return value;
    case PropertyValueTypes.boolean:
      return value === 'true';
    case PropertyValueTypes.number:
      return value;
    case PropertyValueTypes.date:
      return value ? new Date(value) : '';
    case PropertyValueTypes.enum:
      return value;
    case PropertyValueTypes.multipleChoice:
      return value;
    case PropertyValueTypes.list:
      return value;
    case PropertyValueTypes.map:
      return value;
    default:
      return '';
  }
};

const hasNoValue = (value: any) => value === null || value === undefined || value === '';

export const formatValueToString = (value: string, valueType: string) => {
  if (hasNoValue(value)) return ''; // API does not accept null as empty value (interpreted as no changes)
  return value && valueType !== PropertyValueTypes.date ? value.toString() : value;
};

export const formatMapValueToString = (value: string, key: string, valueType: string) => {
  return valueType === PropertyValueTypes.map ? JSON.stringify({ [key]: value }) : '';
};

export const renderValue = (property: PropertyDetail, t: AppTFunction) => {
  if (property.valueType === PropertyValueTypes.boolean) return renderBoolean(property.value, t);
  if (property.valueType === PropertyValueTypes.date) return renderDate(property.value);
  return property.value;
};

export const renderBoolean = (value: string, t: AppTFunction) => {
  if (hasNoValue(value)) return '';
  return value === 'true' ? t('common.yes') : t('common.no');
};

export const renderDate = (value: any): string => {
  if (hasNoValue(value)) return '';

  if (value instanceof Date) {
    return format(value, 'dd/MM/yyyy');
  }

  return format(parseISO(value), 'dd/MM/yyyy');
};

export const multipleChoiceSeparator = ';';

export type PropertyOption = { label: string; value?: string };

export const parseListValueToOptions = (value: string): Array<PropertyOption> => {
  if (!value) return [{ label: '' }];
  const items = value.split(multipleChoiceSeparator).filter(Boolean);
  return items.map((item) => ({ label: item }));
};

export const parseMapValueToOptions = (value: string): Array<PropertyOption> => {
  if (!value) {
    return [{ label: '', value: '' }];
  }

  const pairs = value.split(multipleChoiceSeparator).filter(Boolean);

  if (pairs.length === 0) {
    return [{ label: '', value: '' }];
  }

  return pairs.map((pair) => {
    const [key, ...valueParts] = pair.split(':');
    const mapValue = valueParts.join(':');

    return {
      label: key || '',
      value: mapValue || '',
    };
  });
};

export const convertOptionsToListValue = (options?: Array<PropertyOption>): string => {
  return (options?.map(({ label }) => label.toString()) ?? []).join(multipleChoiceSeparator);
};

export const convertOptionsToMapValue = (options?: Array<PropertyOption>): string => {
  if (!options || options.length === 0) return '';

  return options
    .filter((option) => option.label)
    .map((option) => `${option.label}:${option.value ?? ''}`)
    .join(multipleChoiceSeparator);
};

export const getInitialOptionsForValueType = (valueType: string): Array<PropertyOption> => {
  switch (valueType) {
    case PropertyValueTypes.list:
      return [{ label: '' }];
    case PropertyValueTypes.map:
      return [{ label: '', value: '' }];
    case PropertyValueTypes.enum:
    case PropertyValueTypes.multipleChoice:
      return [{ label: '' }, { label: '' }];
    default:
      return [{ label: '' }];
  }
};

export const formatPropertyValue = (
  options: Array<PropertyOption> | undefined,
  valueType: string,
  fallbackValue?: string
): string => {
  switch (valueType) {
    case PropertyValueTypes.list:
      return convertOptionsToListValue(options);
    case PropertyValueTypes.map:
      return convertOptionsToMapValue(options);
    default:
      return formatValueToString(fallbackValue ?? '', valueType);
  }
};

export const createValueOptions = (
  valueType: string,
  options?: Array<PropertyOption> | null
): { options: Array<string> } | null => {
  if (valueType === PropertyValueTypes.enum || valueType === PropertyValueTypes.multipleChoice) {
    return {
      options: options?.map(({ label }) => label).filter(Boolean) ?? [],
    };
  }
  return null;
};

export const mapOrgUnitsToTransformedProperties = (
  orgUnits: Array<DataownerBasic | LocationOverview>,
  orgUnitType: InformationModelType,
  property: BrandProperty,
  isExternal?: boolean
): Array<TransformedInheritablePropertyValue> => {
  let parentOrgUnitKey: string | undefined;
  let parentOrgUnitName: string | undefined;

  const transformedProperties = orgUnits.map((orgUnit) => {
    // if org unit is a of type location
    if ('dataownerKey' in orgUnit && 'dataownerName' in orgUnit) {
      parentOrgUnitKey = orgUnit.dataownerKey;
      parentOrgUnitName = orgUnit.dataownerName;
    }

    return {
      valueType: property.valueType,
      value: isExternal
        ? orgUnit.externalIds?.find((prop) => prop.key === property.key)?.value ?? ''
        : orgUnit.properties?.find((prop) => prop.key === property.key)?.value ?? '',
      orgUnitType: orgUnitType,
      orgUnitName: orgUnit.name,
      orgUnitKey: orgUnit.key,
      ...(orgUnitType === InformationModelType.Location
        ? {
            parentOrgUnitKey,
            parentOrgUnitName,
          }
        : {}),
    };
  });

  return transformedProperties;
};
